import { IconName } from "../../components/Icon";
import { ProductInfoLinkIds, ProductVideoLinkId } from "../../graphqlQueries/deleteProduct";
import { ProductQueryResult } from "../../graphqlQueries/getCompanyProducts";
import {
  HasuraProductVideo,
  ProductInformation,
  VerifiedLink,
} from "../../graphqlQueries/getProduct";
import verifyLink, {
  VerifyLinkQueryResult,
  VerifyLinkQueryVariables,
} from "../../graphqlQueries/verifyLink";
import {
  AllPossibleProductQueryResult,
  ApolloClientObject,
  CurationStatusesFilter,
  ProductName,
  ProductPublishMode,
  ProductType,
  UserRole,
  dirtyProps,
} from "../../types";
import {
  GetCuratorProductsResult,
  ResultsByCompanyFilter,
  ResultsByFilter,
} from "../../types/curatorProductTypes";
import { getHasuraRoleContext } from "../functions";
import { getProductTypeHeaderKey } from "../localizationUtils";

type ProductNameInformation = {
  productInformations: ProductName[];
};

export type ProductDisplayContent = {
  type: ProductType;
  headerKey: string;
  descKey: string;
  icon: IconName;
};

export const composeResultCount = ({
  approved,
  all,
  new: newProduct,
  approvedAndUpdated,
  updated,
  curationPending,
  pending,
  draft,
}: Partial<GetCuratorProductsResult & ProductQueryResult>): ResultsByCompanyFilter &
  ResultsByFilter => ({
  [CurationStatusesFilter.New]: newProduct?.aggregate.count ?? 0,
  [CurationStatusesFilter.Updated]: updated?.aggregate.count ?? 0,
  [CurationStatusesFilter.Pending]: pending?.aggregate.count ?? 0,
  [CurationStatusesFilter.ApprovedAndUpdated]: approvedAndUpdated?.aggregate.count ?? 0,
  [CurationStatusesFilter.Approved]: approved?.aggregate.count ?? 0,
  [CurationStatusesFilter.Draft]: draft?.aggregate.count ?? 0,
  [CurationStatusesFilter.CurationPending]: curationPending?.aggregate.count ?? 0,
  [CurationStatusesFilter.All]: all?.aggregate.count ?? 0,
  [CurationStatusesFilter.AllCompanyProducts]: all?.aggregate.count ?? 0,
});

export const getDefaultProductInformation = (product: ProductNameInformation) => {
  const enInfo = product.productInformations.find((info) => info.language === "en");
  if (enInfo) {
    return enInfo;
  }
  const fiInfo = product.productInformations.find((info) => info.language === "fi");
  if (fiInfo) {
    return fiInfo;
  }

  return product.productInformations[0];
};

export const getDefaultProductName = (product: ProductNameInformation) => {
  const info = getDefaultProductInformation(product);
  return info?.name ?? "";
};

export enum ExternalProduct {
  Bokun = "bokun",
  Johku = "johku",
}

type ExternalProductDisplayContent = {
  type: ExternalProduct;
  icon: IconName;
};

export const externalProductSources: ExternalProductDisplayContent[] = [
  { icon: "bokunOriginal", type: ExternalProduct.Bokun },
  { icon: "johkuOriginal", type: ExternalProduct.Johku },
];

export type ImportStepCard = {
  icon: IconName;
  title: string;
  description: string;
  width?: string;
  picture?: {
    src: IconName;
    text: string;
  };
};

export type ExternalProductTexts = {
  importFromHeader: string;
  importFromTitle: string;
  importFromDescription: string;
  importFromLabel: string;
  importFromPlaceHolder: string;
  getAccountError: string;
  accountIdLabel: string;
};

export const bokunImportInfoSteps: ImportStepCard[] = [
  {
    description: "importProduct.bokunFirstStepDescription",
    icon: "bokunPink",
    title: "importProduct.bokunFirstStepTitle",
    width: "5rem",
  },
  {
    description: "importProduct.bokunSecondStepDescription",
    icon: "successPink",
    title: "importProduct.bokunSecondStepTitle",
  },
  {
    description: "importProduct.bokunThirdStepDescription",
    icon: "datahubPink",
    picture: {
      src: "bokunExample",
      text: "importProduct.bokunExampleImageText",
    },
    title: "importProduct.bokunThirdStepTitle",
    width: "5.5rem",
  },
  {
    description: "importProduct.bokunFourthStepDescription",
    icon: "bokunDatahubPink",
    title: "importProduct.bokunFourthStepTitle",
    width: "10.25rem",
  },
];

export const johkuImportInfoSteps: ImportStepCard[] = [
  {
    description: "importProduct.johkuFirstStepDescription",
    icon: "johkuPink",
    title: "importProduct.johkuFirstStepTitle",
    width: "5rem",
  },
  {
    description: "importProduct.johkuSecondStepDescription",
    icon: "successPink",
    title: "importProduct.johkuSecondStepTitle",
  },
  {
    description: "importProduct.johkuThirdStepDescription",
    icon: "datahubPink",
    title: "importProduct.johkuThirdStepTitle",
    width: "5.5rem",
  },
  {
    description: "importProduct.johkuFourthStepDescription",
    icon: "johkuDatahubPink",
    title: "importProduct.johkuFourthStepTitle",
    width: "10.25rem",
  },
];

export const productTypes: ProductDisplayContent[] = [
  {
    descKey: "productCategories.experienceDescription",
    headerKey: getProductTypeHeaderKey(ProductType.Experience),
    icon: "skiing",
    type: ProductType.Experience,
  },
  {
    descKey: "productCategories.accommodationDescription",
    headerKey: getProductTypeHeaderKey(ProductType.Accommodation),
    icon: "home",
    type: ProductType.Accommodation,
  },
  {
    descKey: "productCategories.attractionDescription",
    headerKey: getProductTypeHeaderKey(ProductType.Attraction),
    icon: "archway",
    type: ProductType.Attraction,
  },
  {
    descKey: "productCategories.restaurantDescription",
    headerKey: getProductTypeHeaderKey(ProductType.Restaurant),
    icon: "coffee",
    type: ProductType.Restaurant,
  },
  {
    descKey: "productCategories.venueDescription",
    headerKey: getProductTypeHeaderKey(ProductType.Venue),
    icon: "venue",
    type: ProductType.Venue,
  },
  {
    descKey: "productCategories.rentalServiceDescription",
    headerKey: getProductTypeHeaderKey(ProductType.RentalService),
    icon: "car-side",
    type: ProductType.RentalService,
  },
  {
    descKey: "productCategories.eventDescription",
    headerKey: getProductTypeHeaderKey(ProductType.Event),
    icon: "calendar",
    type: ProductType.Event,
  },
  {
    descKey: "productCategories.shopDescription",
    headerKey: getProductTypeHeaderKey(ProductType.Shop),
    icon: "shopping-basket",
    type: ProductType.Shop,
  },
  {
    descKey: "productCategories.transportationDescription",
    headerKey: getProductTypeHeaderKey(ProductType.Transportation),
    icon: "transportation",
    type: ProductType.Transportation,
  },
];

export const getProductLinks = (
  productInformations: ProductInformation[],
  productVideos: HasuraProductVideo[] = []
) => {
  const productLinks = productInformations.reduce<Array<VerifiedLink | null>>(
    (acc, curr) => [...acc, curr.verifiedProductLink],
    []
  );

  const webshopLinks = productInformations.reduce<Array<VerifiedLink | null>>(
    (acc, curr) => [...acc, curr.verifiedWebshopLink],
    []
  );

  const videoLinks = productVideos.reduce<Array<VerifiedLink | null>>(
    (acc, curr) => [...acc, curr.verifiedVideoLink],
    []
  );

  const filterNull = (links: Array<VerifiedLink | null>): Array<VerifiedLink> => {
    return links.reduce<Array<VerifiedLink>>(
      (acc, link) => (link !== null ? [...acc, link] : acc),
      []
    );
  };

  return {
    allLinks: [...filterNull(productLinks), ...filterNull(webshopLinks), ...filterNull(videoLinks)],
    productLinks,
    videoLinks,
    webshopLinks,
  };
};

export const getLinkIds = (
  productInformations: ProductInfoLinkIds[],
  productVideos: ProductVideoLinkId[]
) => {
  const prodLinks = productInformations.reduce<string[]>(
    (acc, curr) =>
      curr.verifiedProductLink !== null ? [...acc, curr.verifiedProductLink.id] : acc,
    []
  );

  const webshopLinks = productInformations.reduce<string[]>(
    (acc, curr) =>
      curr.verifiedWebshopLink !== null ? [...acc, curr.verifiedWebshopLink.id] : acc,
    []
  );

  const videoLinks = productVideos.reduce<string[]>(
    (acc, curr) => (curr.verifiedVideoLink !== null ? [...acc, curr.verifiedVideoLink.id] : acc),
    []
  );

  return [...webshopLinks, ...prodLinks, ...videoLinks];
};

export const hasBrokenLink = (product: AllPossibleProductQueryResult): boolean => {
  if (!product) {
    return false;
  }
  const productLinks = getProductLinks(product.productInformations, product.productVideos);

  return productLinks.allLinks.some((link) => !!link.invalidAt);
};

/*
Due to bug in react-hook-form productDescription is dirty by default.
 */
export const isDirty = ({ dirty: { details, ...rest }, initial, current }: dirtyProps) => {
  if (!initial || !current) {
    return false;
  } else if (details?.length === 1 && details[0]?.productDescription) {
    return false;
  } else if (initial.length !== current.length) {
    return true;
  } else {
    return initial.some(
      ({ language, description }) =>
        current.find((c) => c.language === language)?.productDescription !== description
    );
  }
};

type LinkValidatorArguments = {
  apolloClient: ApolloClientObject;
  link: string | undefined;
  errorMessage: string;
  publishMode: ProductPublishMode;
  isDmo: boolean;
};

export const linkValidator = async ({
  apolloClient,
  link,
  errorMessage,
  publishMode,
  isDmo,
}: LinkValidatorArguments) => {
  if (!link || publishMode === "draft") {
    return true;
  }
  const userRole = isDmo ? UserRole.ManageCuration : UserRole.ManageProducts;
  try {
    const validationResult = await apolloClient.query<
      VerifyLinkQueryResult,
      VerifyLinkQueryVariables
    >({
      context: getHasuraRoleContext(userRole),
      query: verifyLink,
      variables: {
        link: link,
      },
    });

    if (validationResult.data.VerifyLink.isValid) {
      return true;
    }
  } catch (error) {
    console.error("Link validation error", { error, link });
  }

  return errorMessage;
};

export const shouldAddCapacity = (type?: ProductType) => {
  return (
    type === ProductType.Venue ||
    type === ProductType.Experience ||
    type === ProductType.Transportation
  );
};

export const shouldAddDuration = (type?: ProductType) => {
  return type === ProductType.Experience;
};
